import { axios, getEnvVar, initSentry } from '@utils';
import { createApp } from 'vue';
import ActionCableVue from 'actioncable-vue';
import VueSweetalert2 from 'vue-sweetalert2';
import FloatingVue from 'floating-vue';
import Navigation from '@parent/components/navigation/navigation';
import VueObserveVisibility from 'vue-observe-visibility';
import Notification from '@components/notification';
import ToastContainer from '@components/toast-notifications/toast-container.vue';
import router from '@routes/parents.router';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import store from '@parent/stores/parent.store';
import Cloudinarize from '@plugins/cloudinarize';
import SyncRouteStore, { parentConfig } from '@plugins/syncRouteStore';
import UserAccessPlugin from '@plugins/userAccess';
import PortalVue from 'portal-vue';
import ModalTarget from '@components/modal/modal-target.vue';
import ProfileMap from '@profile/components/map.vue';
import ProfileCta from '@profile/components/cta.vue';
import ProfileOpinionBar from '@profile/components/opinion-bar.vue';
import ProfileButtonReviews from '@profile/components/button-reviews.vue';
import ProfileGallery from '@profile/components/gallery.vue';
import ProfileOffers from '@profile/components/offers.vue';
import ProfileWaitlistMobile from '@profile/components/waitlist-mobile.vue';
import ProfileSpotOffer from '@profile/components/spot-offer.vue';
import ProfileProgramActions from '@profile/components/program-actions.vue';
import ExpandableSpotList from '@profile/components/expandable-spot-list.vue';
import ExpandableScheduleList from '@profile/components/expandable-schedule-list.vue';
import AuthMixin from '@profile/mixins/auth';
import payMeFlow from '@parent/components/payments/send-payment/pay-me-flow.vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import PaymentRequestSummary from '@parent/components/payments/request-payment/payment-request-summary.vue';
import ReadMore2 from '../src/common/components/read-more/read-more-2.vue';

initSentry();

const app = {
  router,
  store,

  mixins: [
    AuthMixin,
  ],

  created() {
    this.onWindowResize();
    window.addEventListener('resize', this.onWindowResize);
    this.$sync.setup(this.$router, this.$store);
  },

  async mounted() {
    axios.defaults.headers.common.Accepts = '*/*';

    const metaCsrf = document.querySelector('meta[name="csrf-token"]');
    if (metaCsrf) {
      axios.defaults.headers.common['X-CSRF-Token'] = metaCsrf.getAttribute('content');
    }

    if (this.isAuthorized) {
      await this.$store.dispatch('init');
      this.$cable.subscribe({
        channel: 'ConversationsChannel',
        type: 'User',
        id: this.$store.state.user.current.id,
      });
    }

    // style scrollbars when visible
    const isScrollbarHidden = window.innerWidth === document.body.clientWidth;
    if (!isScrollbarHidden) {
      document.body.classList.add('styled-scrollbar');
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },

  components: {
    Navigation,
    Notification,
    ToastContainer,
    ModalTarget,
    ProfileMap,
    ProfileCta,
    ProfileOpinionBar,
    ProfileButtonReviews,
    ProfileGallery,
    ProfileOffers,
    ProfileWaitlistMobile,
    ProfileSpotOffer,
    ProfileProgramActions,
    payMeFlow,
    ExpandableSpotList,
    PaymentRequestSummary,
    ReadMore2,
    ExpandableScheduleList,
  },

  data() {
    return {
      showProfile: true,
    };
  },

  watch: {
    $route(to) {
      this.showProfile = to.name === 'facility-profile';
    },
  },

  methods: {
    reload() {
      window.location.reload();
    },
    onWindowResize() {
      this.$store.commit('setIsMobile', window.innerWidth < 768);
    },
    disconnect() {
      this.$cable.connection.disconnect();
    },
  },

  channels: {
    MessagesChannel: {
      received(message) {
        this.$store.dispatch('messaging/newMessageReceived', message);
      },
    },
    ConversationsChannel: {
      received(conversation) {
        this.$store.dispatch('messaging/newConversationReceived', conversation);
      },
    },
  },
};

createApp(app)
  .use(store)
  .use(router)
  .use(PortalVue)
  .use(VueSweetalert2)
  .use(VueObserveVisibility)
  .use(SyncRouteStore, parentConfig)
  .use(UserAccessPlugin)
  .use(Cloudinarize)
  .use(VueReCaptcha, {
    siteKey: getEnvVar('RECAPTCHA_SITE_KEY'),
    loaderOptions: {
      renderParameters: {
        badge: 'bottomleft',
      },
    },
  })
  .use(FloatingVue, {
    themes: {
      'kinside-info': {
        $extend: 'tooltip',
        triggers: ['hover', 'touch'],
        autoHide: false,
        placement: 'top',
      },
    },
  })
  .use(ActionCableVue, {
    debug: getEnvVar('NODE_ENV') === 'development',
    debugLevel: 'error',
    connectionUrl: `${getEnvVar('WEBSOCKET_BASE_URL')}/cable`,
    connectImmediately: true,
    store,
  })
  .use(VueGoogleMaps, {
    load: {
      key: getEnvVar('GOOGLE_API_KEY'),
      libraries: 'places',
    },
  })
  .mount('#parents');
